import { Button } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import Redux from 'app/interface/redux'
import Basic from 'app/redux/actions/pages/business/basic'
import Connect from 'app/redux/connect'
import { businessManagerService } from 'app/service/business-manager'
import {
  CommonFBBusinessObjectFields,
  FacebookAssetNames,
  SystemUserIDs,
} from 'app/service/business-manager/BusinessManager.types'
import React, { useCallback, useEffect, useState } from 'react'
import Toggle from 'react-switch'
import { toast } from 'react-toastify'
import FacebookObjectSelector, { FBObjectTypeString } from './facebook-object-selector'
import Popover from 'components/reusable/popover'

export interface MMFacebookVerificationFields {
  fb_account_id: string
  fb_page_id: string
  fb_dataset_id: string
  fb_offline_id?: string
  instagram_id?: string
}

interface FacebookProps extends Redux {
  switchMode: () => void
  new: boolean
}

const Facebook = (props: FacebookProps) => {
  const { main } = props.pages.business
  const fbConnected = props.core.session.user!.fbConnected ?? false

  const [accountID, setAccountID] = useState<string | undefined>(main.fb_account_id)
  const [pageID, setPageID] = useState<string | undefined>(main.fb_page_id ?? undefined)
  const [datasetID, setDatasetID] = useState<string | undefined>(main.fb_dataset_id ?? undefined)
  const [offlineID, setOfflineID] = useState<string | undefined>(
    main.use_offline_id ? main.offline_event_set_id ?? undefined : undefined
  )
  const [instagramID, setInstagramID] = useState<string | undefined>(main.instagram_id ?? undefined)
  const [useOfflineDataset, setUseOfflineDataset] = useState<boolean>(main.use_offline_id)
  const [verifying, setVerifying] = useState<boolean>(false)
  const [verified, setVerified] = useState<boolean>(false)
  const [selectionsValid, setSelectionsValid] = useState<boolean>(false)

  const [cacheKey, setCacheKey] = useState(0)

  const hasAllRequiredSelections = useCallback(() => {
    const hasRequiredSelections = !!accountID && !!pageID && !!datasetID
    return hasRequiredSelections
  }, [accountID, datasetID, pageID])

  const assignFBObject = (
    fbObject: CommonFBBusinessObjectFields | undefined,
    field: FBObjectTypeString
  ) => {
    switch (field) {
      case 'ad_account':
        setAccountID(fbObject?.id)
        Basic.update('fb_account_id', fbObject?.id)
        break
      case 'page':
        setPageID(fbObject?.id)
        Basic.update('fb_page_id', fbObject?.id)
        break
      case 'pixel':
        setDatasetID(fbObject?.id)
        Basic.update('fb_dataset_id', fbObject?.id)
        break
      case 'offline':
        setOfflineID(fbObject?.id)
        Basic.update('offline_event_set_id', fbObject?.id)
        break
      case 'ig':
        setInstagramID(fbObject?.id)
        Basic.update('instagram_id', fbObject?.id)
        break
    }
  }

  useEffect(() => {
    setSelectionsValid(hasAllRequiredSelections())
  }, [accountID, pageID, datasetID, hasAllRequiredSelections])

  const setVerifiedFBConnections = (verified: SystemUserIDs & FacebookAssetNames) => {
    // This is awful, but Basic.update can't update multiple at once and mapping these seems pointless.
    Basic.update('adaccount_system_user_id', verified.adaccountSystemUserID)
    Basic.update('page_system_user_id', verified.pageSystemUserID)
    Basic.update('dataset_system_user_id', verified.datasetSystemUserID)
    Basic.update('offline_system_user_id', verified.offlineSystemUserID)
    Basic.update('instagram_system_user_id', verified.instagramSystemUserID)
    Basic.update('fb_account_name', verified.fbAccountName)
    Basic.update('fb_page_name', verified.fbAccountName)
    Basic.update('fb_dataset_name', verified.fbDatasetName)
    Basic.update('fb_offline_name', verified.fbOfflineEventSetName)
    Basic.update('instagram_name', verified.fbInstagramName)
  }

  const verifyFBConnection = async () => {
    if (!accountID || !pageID || !datasetID) {
      toast.warn('Select an Ad Account, Page, and Dataset before verifying access.')
      return
    }
    setVerifying(true)
    try {
      const result = await businessManagerService.verifyFBConnection({
        fb_account_id: accountID,
        fb_page_id: pageID,
        fb_dataset_id: datasetID,
        fb_offline_id: offlineID,
        instagram_id: instagramID,
      })

      setVerifiedFBConnections(result)

      toast.success('Verified access to your Facebook assets.')
      setVerified(true)
    } catch (e) {
      console.error(e)
      toast.error('Access verification failed. Check your access in Facebook.')
      setVerified(false)
    } finally {
      setVerifying(false)
    }
  }

  const handleRefreshFacebookAccess = async () => {
    await businessManagerService.clearFacebookAccessCache()
    setCacheKey(cacheKey => cacheKey + 1)
  }

  return (
    <>
      <div className="d-flex justify-content-between mb-4">
        <h4 className="px-4">
          <i className="fab fa-facebook"></i>&nbsp;Facebook Builder
        </h4>
        <div className="d-flex align-items-center">
          <Button
            onClick={handleRefreshFacebookAccess}
            className="mr-2"
            variant="contained"
            color="secondary"
          >
            Refresh Facebook Access
          </Button>
          <Popover>
            Marketing Milk saves what Facebook Ad Accounts and Pages you have access to the first
            time you load this page. If you have updated your Ad Account or Page access in Facebook
            Business Manager, click this button to select those assets.
            <br />
            <br />
            <strong>
              Warning: This can be slow if you have access to many Ad Accounts and Pages.
            </strong>
          </Popover>
        </div>
      </div>
      {!fbConnected && (
        <Alert
          style={{
            width: 'fit-content',
            marginLeft: '1.5rem',
            marginTop: '1rem',
            marginBottom: '1.5rem',
            fontSize: '1rem',
          }}
          severity="error"
        >
          You cannot grant access to Facebook assets if you aren't logged in using Facebook.
        </Alert>
      )}
      <div className="row px-3 pb-3">
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FacebookObjectSelector
            key={cacheKey}
            title="Facebook Ad Account"
            disabled={!(props.new && fbConnected)}
            type="ad_account"
            selectedFbObjectName={main.fb_account_name}
            selectedFbObjectID={accountID}
            assignFbObject={assignFBObject}
            tooltipText="Account to manage paid advertising campaigns and billing on Facebook and Instagram"
          />
        </div>
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FacebookObjectSelector
            key={cacheKey}
            title="Facebook Page"
            disabled={!fbConnected}
            type="page"
            selectedFbObjectName={main.fb_page_name}
            selectedFbObjectID={pageID}
            assignFbObject={assignFBObject}
            tooltipText="Public-facing profile for businesses, brands, or organizations on Facebook"
          />
        </div>
      </div>
      <div className="row px-3 pb-3">
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FacebookObjectSelector
            title="Instagram Account"
            disabled={!fbConnected}
            type="ig"
            accountID={accountID}
            selectedFbObjectID={instagramID}
            selectedFbObjectName={main.instagram_name}
            assignFbObject={assignFBObject}
          />
        </div>
      </div>
      <hr />
      <div className="col-md-6 col-sm-12 col-xs-12">
        <label>
          <p>Use Separate Dataset for Offline Data?</p>
          <Toggle
            checked={Boolean(useOfflineDataset)}
            onChange={value => {
              if (!value) {
                setOfflineID(undefined)
                Basic.update('offline_event_set_id', null)
              }

              setUseOfflineDataset(value)
              Basic.update('use_offline_id', value)
            }}
            disabled={!fbConnected}
          />
        </label>
      </div>
      <div className="row px-3 pb-3">
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FacebookObjectSelector
            title="Dataset"
            disabled={!fbConnected}
            type="pixel"
            accountID={accountID}
            selectedFbObjectName={main.fb_dataset_name}
            selectedFbObjectID={datasetID}
            assignFbObject={assignFBObject}
            tooltipText="A collection of data generated from Facebook campaigns, pages, or offline-event uploads."
          />
        </div>
        {useOfflineDataset && (
          <div className="col-md-6 col-sm-12 col-xs-12">
            <FacebookObjectSelector
              title="Offline Dataset"
              disabled={!fbConnected || !useOfflineDataset}
              type="offline"
              accountID={accountID}
              selectedFbObjectName={main.fb_offline_name}
              selectedFbObjectID={offlineID}
              assignFbObject={assignFBObject}
              tooltipText="A dataset used when online and offline data need to be managed separately."
            />
          </div>
        )}
      </div>
      <div className="col-sm-12 py-5 d-flex flex-row-reverse justify-content-between align-items-center flex-grow-0">
        <Button
          className="py-2"
          data-testid="create-user-btn"
          variant="contained"
          color="primary"
          startIcon={<i className="fab fa-wrench"></i>}
          onClick={verifyFBConnection}
          disabled={!fbConnected || !selectionsValid || verifying}
        >
          <span>Verify{verifying ? 'ing' : ''} Access</span>
        </Button>
        {verified && (
          <Alert
            className="py-0"
            style={{
              width: 'fit-content',
              fontSize: '1.1rem',
            }}
            severity="success"
          >
            Verified access to your Facebook assets. <strong>Save</strong> to keep these settings.
          </Alert>
        )}
      </div>
      <div className="col-sm-12 pb-5">
        <h5>Having Issues?</h5>
        <p>
          This feature is currently in beta. If you are having issues, please report them to the
          team and revert back to using Facebook Authorizers for the time being.
        </p>
        <Button
          data-testid="create-user-btn"
          variant="contained"
          color="secondary"
          onClick={() => props.switchMode()}
        >
          <span>Revert To Facebook Authorizers</span>
        </Button>
      </div>
    </>
  )
}

export default Connect(Facebook)
